import { unwrap } from "@sagot/api-client";
import { BILLING_CURRENCIES } from "@sagot/constants";
import { useTranslation } from "@sagot/i18n";
import { Button, Field, Select, Skeleton, toast } from "@sagot/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useErrorMessage } from "../../../../lib/api-errors";
import { CountrySelect } from "../../../../lib/region-pickers";
import { usePermissions } from "../../../../session/permissions";
import { useApi } from "../../../../session/session-provider";
import { useWorkspaceId } from "../../../../session/workspace-context";
import { useFormatters, wsKeys, type RegionOut } from "../../shared";
import { CardActions, useCanManage } from "./panel-kit";

function useRegion(enabled: boolean) {
  const api = useApi();
  const wsId = useWorkspaceId();
  return useQuery({ queryKey: wsKeys.billingRegion(wsId), queryFn: () => unwrap(api.GET("/v1/billing/region")), enabled });
}

function RegionForm({ region }: { region: RegionOut }) {
  const { t } = useTranslation();
  const api = useApi();
  const wsId = useWorkspaceId();
  const fmt = useFormatters();
  const queryClient = useQueryClient();
  const message = useErrorMessage();
  const manage = useCanManage("billing.manage");
  const [country, setCountry] = useState(region.country);
  const [currency, setCurrency] = useState(region.billing_currency);
  const save = useMutation({
    mutationFn: () => unwrap(api.PUT("/v1/billing/region", { body: { country, billing_currency: currency === "USD" ? "USD" : "PHP" } })),
    onSuccess: async (saved) => {
      toast.success(saved.effective_at ? t("settings.region.savedLater", { date: fmt.date(saved.effective_at) }) : t("settings.region.saved"));
      await queryClient.invalidateQueries({ queryKey: wsKeys.billing(wsId) });
    },
    onError: (caught) => toast.error(message(caught)),
  });
  const dirty = country !== region.country || currency !== region.billing_currency;
  const locked = !manage.allowed;
  return (
    <>
      <fieldset className="card-body space-y-4" disabled={locked}>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <Field label={t("settings.region.country")} hint={t("settings.region.countryHint")} id="billing-country">
            <CountrySelect id="billing-country" value={country} saved={region.country} onChange={setCountry} disabled={locked} />
          </Field>
          <Field label={t("settings.region.currency")} hint={t("settings.region.currencyHint")}>
            <Select value={currency} onChange={(event) => { setCurrency(event.target.value); }}>
              {BILLING_CURRENCIES.map((code) => (
                <option key={code} value={code}>
                  {t(`settings.region.currencies.${code}`)}
                </option>
              ))}
            </Select>
          </Field>
        </div>
        <p className="text-xs text-fg-subtle">
          {t("settings.region.tax", { label: region.tax.label })}
          {region.effective_at ? ` ${t("settings.region.pending", { currency: region.billing_currency, date: fmt.date(region.effective_at) })}` : ""}
        </p>
      </fieldset>
      <CardActions>
        <Button type="button" disabled={locked || !dirty} loading={save.isPending} title={manage.reason} onClick={() => { save.mutate(); }}>
          {t("settings.region.save")}
        </Button>
      </CardActions>
    </>
  );
}

/** Settings → Organization: billing country + currency (DEC-042, `PUT /v1/billing/region`). */
export function BillingRegionCard() {
  const { t } = useTranslation();
  const permissions = usePermissions();
  const canView = permissions.can("billing.view");
  const region = useRegion(canView);
  return (
    <section className="card" aria-labelledby="region-title" data-testid="billing-region-card">
      <div className="card-header">
        <div>
          <h2 id="region-title" className="card-title">
            {t("settings.region.title")}
          </h2>
          <p className="mt-0.5 text-xs text-fg-subtle">{t("settings.region.subtitle")}</p>
        </div>
      </div>
      {!canView ? (
        <p className="card-body text-sm text-fg-muted">{t("settings.region.noAccess")}</p>
      ) : region.data ? (
        <RegionForm key={[region.data.country, region.data.billing_currency, region.data.effective_at].join("|")} region={region.data} />
      ) : region.isError ? (
        <p className="card-body text-sm text-fg-muted">{t("settings.region.loadError")}</p>
      ) : (
        <div className="card-body">
          <Skeleton className="h-16 w-full" />
        </div>
      )}
    </section>
  );
}
